import type { ZodIssue } from 'zod'
import type { EntityLogicalName } from './client'

export type MissingDataRecord = {
  /** null when the record has no usable id — message-only, not fixable in-app */
  id: string | null
  /** Raw Dataverse payload, used to prefill the fix form */
  raw: Record<string, unknown>
  issues: ZodIssue[]
}

/**
 * A query's zod validation failed because required Dataverse data is missing or
 * invalid. Carries enough context (entity, record id, raw values, issues) for
 * the fix-missing-data dialog to repair the record in-app.
 */
export class MissingRequiredDataError extends Error {
  override name = 'MissingRequiredDataError'

  constructor(
    public readonly entity: EntityLogicalName,
    public readonly records: MissingDataRecord[]
  ) {
    // Display message caps at 3 issues; the full list stays on `records`
    super(
      [...new Set(records.flatMap((record) => record.issues.map((issue) => issue.message)))].slice(0, 3).join('\n')
    )
  }
}

const FIXABLE_ACCOUNT_FIELDS = ['address1_line1', 'address1_city', 'address1_stateorprovince', 'address1_postalcode']
const FIXABLE_QUOTE_FIELDS = [
  '_pricelevelid_value',
  'dsy_Branch',
  'dsy_salesrep',
  '_dsy_contact_value',
  'dsy_billingcontact',
  'dsy_type',
  'dsy_saletype',
]

function isFixableIssue(entity: EntityLogicalName, issue: ZodIssue): boolean {
  switch (entity) {
    case 'accounts':
      return FIXABLE_ACCOUNT_FIELDS.includes(String(issue.path[0]))
    case 'contacts':
      return issue.path[0] === 'fullname'
    case 'dsy_accountrooms':
      return issue.path[0] === 'dsy_name'
    case 'quotes':
      return FIXABLE_QUOTE_FIELDS.includes(String(issue.path[0]))
    default:
      return false
  }
}

/** True when at least one failed field can be repaired by the fix-missing-data dialog. */
export function hasFixableIssues(error: MissingRequiredDataError): boolean {
  return error.records.some((record) => record.id && record.issues.some((issue) => isFixableIssue(error.entity, issue)))
}

/** Messages for issues the dialog can NOT repair — shown so a successful save that
 * won't fully resolve the error screen doesn't read as "my fix didn't take". */
export function getUnfixableIssueMessages(error: MissingRequiredDataError): string[] {
  return [
    ...new Set(
      error.records.flatMap((record) =>
        record.issues.filter((issue) => !record.id || !isFixableIssue(error.entity, issue)).map((issue) => issue.message)
      )
    ),
  ]
}
